import type { ApiAsset, ApiTitle } from "@lumi/data";
import { useEffect, useState } from "react";
import { useAuthenticatedClient } from "../../auth/AuthContext";
import { getPreloadedPlayerData } from "../utils/playerPreload";

interface UseAssetLoaderProps {
  id: string | undefined;
  assetId?: string; // Optional assetId from Continue Watching
  assetKind?: string; // Optional assetKind to filter by type (e.g., 'TRAILER')
}

/**
 * Select the appropriate asset based on priority:
 * 1. Specific assetId (from Continue Watching)
 * 2. Asset kind filter (e.g., TRAILER)
 * 3. FEATURE asset (default)
 * 4. First available asset (fallback)
 */
function selectAsset(assets: ApiAsset[], requestedAssetId?: string, assetKind?: string): ApiAsset {
  // Priority 1: Specific asset ID from Continue Watching
  if (requestedAssetId) {
    const found = assets.find((a) => a.id === requestedAssetId);
    if (found) {
      console.log("🎯 Using requested asset from Continue Watching:", requestedAssetId);
      return found;
    }
    console.warn("⚠️ Requested asset not found, falling back to FEATURE asset");
  }

  // Priority 2: Filter by kind (e.g., TRAILER)
  if (assetKind) {
    const found = assets.find((a) => a.kind === assetKind);
    if (found) {
      console.log(`🎬 Using ${assetKind} asset`);
      return found;
    }
    console.warn(`⚠️ No ${assetKind} asset found, falling back to FEATURE`);
  }

  // Priority 3: FEATURE asset (default for Play button)
  const featureAsset = assets.find((a) => a.kind === "FEATURE");
  return featureAsset || assets[0]; // Priority 4: First available
}

/**
 * Create demo fallback asset when no assets are available
 */
function createDemoAsset(): ApiAsset {
  console.log("⚠️ No assets found for title, using demo video");
  return {
    id: "demo-asset",
    hlsUrl: "https://demo.unified-streaming.com/k8s/features/stable/video/tears-of-steel/tears-of-steel.ism/.m3u8",
    subtitles: [{ lang: "en", url: "", forced: false }],
    thumbnailVttUrl: null,
  };
}

/**
 * Transform API asset to working asset format with logging
 */
function transformAsset(selectedAsset: ApiAsset, requestedAssetId?: string, assetKind?: string): ApiAsset {
  console.log("🎬 Using asset from database:", {
    kind: String(selectedAsset.kind ?? "unknown"),
    hlsUrl: selectedAsset.hlsUrl.substring(0, 100) + "...",
    expiresAt: selectedAsset.expiresAt ?? "no expiration",
    isFeature: selectedAsset.kind === "FEATURE",
    fromContinueWatching: !!requestedAssetId,
    fromAssetKind: !!assetKind,
  });

  return {
    id: selectedAsset.id,
    hlsUrl: selectedAsset.hlsUrl,
    subtitles: selectedAsset.subtitles || [{ lang: "en", url: "", forced: false }],
    thumbnailVttUrl: selectedAsset.thumbnailVttUrl,
    kind: selectedAsset.kind,
    ...(selectedAsset.expiresAt && { expiresAt: selectedAsset.expiresAt }),
  };
}

export function useAssetLoader({ id, assetId: requestedAssetId, assetKind }: UseAssetLoaderProps) {
  const authenticatedClient = useAuthenticatedClient();
  const [title, setTitle] = useState<ApiTitle | null>(null);
  const [asset, setAsset] = useState<ApiAsset | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const handleRetryLoad = () => {
    setError(null);
    setLoading(true);
    setRetryCount((prev) => prev + 1);
  };

  useEffect(() => {
    async function loadTitleAndAsset(): Promise<void> {
      if (!id) {
        setError("No title ID provided");
        setLoading(false);
        return;
      }

      if (!authenticatedClient) {
        setError("Authentication required");
        setLoading(false);
        return;
      }

      try {
        // Check if data was preloaded during transition
        const preloaded = getPreloadedPlayerData(id, {
          assetId: requestedAssetId,
          assetKind,
        });

        if (preloaded) {
          setTitle(preloaded.title);
          setAsset(preloaded.asset);
          setLoading(false);
          return;
        }

        console.log("🔍 Loading title and asset for:", id, requestedAssetId ? `with assetId: ${requestedAssetId}` : "");

        // Use authenticated client which handles token refresh automatically
        const titles = await authenticatedClient.listTitles();
        const foundTitle = titles.find((t) => t.id === id || t.slug === id);

        if (!foundTitle) {
          setError("Title not found");
          setLoading(false);
          return;
        }

        console.log("✅ Title found:", foundTitle);
        setTitle(foundTitle);

        // Load assets - authenticated client will auto-refresh if token expired
        console.log("🔍 Loading assets for title:", foundTitle.id);
        const assets = await authenticatedClient.listTitleAssets(foundTitle.id);
        console.log("✅ Assets loaded:", assets);

        if (!assets || assets.length === 0) {
          setAsset(createDemoAsset());
        } else {
          const selectedAsset = selectAsset(assets, requestedAssetId, assetKind);
          const workingAsset = transformAsset(selectedAsset, requestedAssetId, assetKind);
          setAsset(workingAsset);
        }

        setLoading(false);
      } catch (err) {
        console.error("❌ Failed to load title and asset:", err);
        setError("Failed to load video");
        setLoading(false);
      }
    }

    void loadTitleAndAsset();
  }, [id, requestedAssetId, assetKind, retryCount, authenticatedClient]);

  return {
    title,
    asset,
    loading,
    error,
    handleRetryLoad,
  };
}
